import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  TextField,
  InputAdornment,
  TablePagination,
  IconButton,
  Switch,
  Box,
  MenuItem,
  Tooltip,
} from "@mui/material";
import Scrollbar from "../../../custom-scroll/Scrollbar";
import SearchIcon from "@mui/icons-material/Search";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloseIcon from "@mui/icons-material/Close";
import PropTypes from "prop-types";
import { AgentsManagementActionMenuTable } from "..";
import { AgentIdCell } from "./AgentIdCell";
import { ROLE_OPTIONS, STATUS_OPTIONS } from "../constants";

const headCellSx = {
  fontWeight: 600,
  color: 'text.secondary',
  fontSize: '0.8rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  borderBottom: '2px solid',
  borderColor: 'divider',
};

export const AgentsManagementTable = ({
  agents = [],
  loading = false,
  page = 1,
  pageSize = 10,
  total = 0,
  onPageChange,
  onPageSizeChange,
  onEditAgent,
  onChangeAgentStatus,
  onChangeAgentAccess,
  onChangePassword,
  statusUpdatingIds = [],
  accessUpdatingIds = [],
  searchTerm = '',
  onSearchChange,
  onSearchSubmit,
  searchPerformed = false,
  roleFilter = 'todos',
  statusFilter = 'todos',
  onRoleFilterChange,
  onStatusFilterChange,
  canChangeAgentStatus = false,
  canEditAgent = false,
  canChangePassword = false,
  serverPagination = false,
}) => {
  const canSearch = Boolean(searchTerm && searchTerm.trim().length > 0);
  const startIndex = (page - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedAgents = serverPagination ? agents : agents.slice(startIndex, endIndex);

  const getRoleChipColor = (role) => {
    if (!role) return "default";
    const normalized = role.trim().toLowerCase();
    if (normalized === "administrator" || normalized === "admin") {
      return "primary";
    }
    return "default";
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: { xs: "flex-start", sm: "space-between" },
          alignItems: { xs: "stretch", sm: "center" },
          mt: 1,
          mb: 2,
          gap: 2,
          flex: "0 0 auto",
        }}
      >
        <Box sx={{ display: "flex", width: { xs: "100%", sm: "60%" }, gap: 2, alignItems: 'center' }}>
          <TextField
            sx={{ width: "65%" }}
            placeholder="Buscar por nombre o correo"
            value={searchTerm}
            onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: canSearch ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    aria-label="limpiar búsqueda"
                    onClick={() => onSearchChange && onSearchChange("")}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : undefined,
            }}
          />
          {canSearch && (
            <IconButton
              color="primary"
              onClick={() => onSearchSubmit && onSearchSubmit(searchTerm.trim())}
              aria-label="buscar por nombre o correo"
              size="small"
              sx={{ ml: 1 }}
            >
              <ArrowForwardIcon />
            </IconButton>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            width: { xs: "100%", sm: "40%" },
            gap: 1.5,
            justifyContent: { xs: "flex-start", sm: "flex-end" },
            alignItems: "center",
          }}
        >
          <Box sx={{ width: { xs: "100%", sm: 180 } }}>
            <TextField
              select
              fullWidth
              label="Filtrar por rol"
              value={roleFilter}
              onChange={(e) => onRoleFilterChange && onRoleFilterChange(e.target.value)}
              size="small"
            >
              {ROLE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ width: { xs: "100%", sm: 180 } }}>
            <TextField
              select
              fullWidth
              label="Filtrar por estado"
              value={statusFilter}
              onChange={(e) => onStatusFilterChange && onStatusFilterChange(e.target.value)}
              size="small"
            >
              {STATUS_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>
      </Box>

      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', flex: 1, minHeight: 0 }}>
      <Paper
        sx={{
          width: '100%',
          maxWidth: 1200,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          flex: '1 1 auto',
          minHeight: 0,
          borderRadius: 2,
        }}
      >
        <TableContainer
          component={Scrollbar}
          sx={{
            flex: "1 1 auto",
            minHeight: 0,
          }}
        >
          <Table
            stickyHeader
            sx={{ minWidth: 1000 }}
            aria-label="tabla de agentes"
          >
            <TableHead>
              <TableRow sx={{ backgroundColor: 'background.default' }}>
                <TableCell sx={headCellSx}>Id</TableCell>
                <TableCell sx={headCellSx}>Nombre Agente</TableCell>
                <TableCell sx={headCellSx}>Correo</TableCell>
                <TableCell align="center" sx={headCellSx}>Rol</TableCell>
                <TableCell sx={headCellSx}>Sucursal</TableCell>
                <TableCell sx={headCellSx}>Contactos asignados</TableCell>
                <Tooltip title="Indica el estado de conexión del agente. Puedes cambiar su estado usando el interruptor." arrow placement="top-start">
                  <TableCell sx={headCellSx}>Estado</TableCell>
                </Tooltip>
                <Tooltip title="Indica si el agente tiene acceso. Puedes cambiar su acceso usando el interruptor." arrow placement="top-start">
                  <TableCell sx={headCellSx}>Acceso</TableCell>
                </Tooltip>
                <TableCell align="center" sx={headCellSx}>Acciones</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Box sx={{ color: "text.secondary" }}>
                      Cargando agentes...
                    </Box>
                  </TableCell>
                </TableRow>
              ) : agents.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <Box sx={{ color: "text.secondary" }}>
                      {searchPerformed && canSearch
                        ? "No se encontró agente que coincida con la búsqueda"
                        : "No hay agentes disponibles por el momento"}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedAgents.map((agent) => (
                  <TableRow
                    key={agent._id}
                    hover
                    sx={{
                      '&:last-child td': { borderBottom: 0 },
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <TableCell>
                      <AgentIdCell agentId={agent._id} />
                    </TableCell>
                    <TableCell component="th" scope="row">
                      {agent.display_name || "-"}
                    </TableCell>
                    <TableCell>{agent.email || "-"}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={agent.role || "-"}
                        color={getRoleChipColor(agent.role)}
                        size="small"
                        sx={{
                          fontWeight: "medium",
                          minWidth: 90,
                        }}
                      />
                    </TableCell>
                    <TableCell>{agent.branch || ""}</TableCell>
                    <TableCell align="center">{agent.assigned_contacts ?? 0}</TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                        <Typography variant="body2">
                          {agent?.agent_disponibility === 'Activo' ? 'Activo' : 'Inactivo'}
                        </Typography>
                        <Switch
                          size="small"
                          checked={agent?.agent_disponibility === 'Activo'}
                          onChange={() => onChangeAgentStatus && onChangeAgentStatus(agent)}
                          color="primary"
                          disabled={statusUpdatingIds.includes(agent._id) || !canChangeAgentStatus}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                        <Typography variant="body2">
                          {agent?.is_active ? 'Activo' : 'Inactivo'}
                        </Typography>
                        <Switch
                          size="small"
                          checked={Boolean(agent?.is_active)}
                          onChange={() => onChangeAgentAccess && onChangeAgentAccess(agent)}
                          color="primary"
                          disabled={accessUpdatingIds.includes(agent._id) || !canEditAgent}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <AgentsManagementActionMenuTable
                        onEdit={() => onEditAgent && onEditAgent(agent)}
                        onChangePassword={() => onChangePassword && onChangePassword(agent)}
                        canEdit={canEditAgent}
                        canChangePassword={canChangePassword}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          onPageChange={(e, newPage) => onPageChange(newPage + 1)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            const newSize = parseInt(e.target.value, 10);
            onPageSizeChange(newSize);
          }}
          sx={{
            flex: "0 0 auto",
            overflow: "hidden",
          }}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
          labelRowsPerPage="Filas por página:"
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
        />
      </Paper>
      </Box>
    </Box>
  );
};

AgentsManagementTable.propTypes = {
  agents: PropTypes.array,
  loading: PropTypes.bool,
  page: PropTypes.number,
  pageSize: PropTypes.number,
  total: PropTypes.number,
  onPageChange: PropTypes.func,
  onPageSizeChange: PropTypes.func,
  onEditAgent: PropTypes.func,
  onChangeAgentStatus: PropTypes.func,
  onChangeAgentAccess: PropTypes.func,
  onChangePassword: PropTypes.func,
  statusUpdatingIds: PropTypes.array,
  accessUpdatingIds: PropTypes.array,
  searchTerm: PropTypes.string,
  onSearchChange: PropTypes.func,
  onSearchSubmit: PropTypes.func,
  searchPerformed: PropTypes.bool,
  roleFilter: PropTypes.string,
  statusFilter: PropTypes.string,
  onRoleFilterChange: PropTypes.func,
  onStatusFilterChange: PropTypes.func,
  canChangeAgentStatus: PropTypes.bool,
  canEditAgent: PropTypes.bool,
  canChangePassword: PropTypes.bool,
  serverPagination: PropTypes.bool,
};
